<template>
  <sb-modal
    :active="openModal"
    :escape-trigger="true"
    :click-outside-trigger="false"
    class="import-products-modal"
    @close="closeModal"
  >
    <div class="import-products-modal__body">
      <div v-if="loading" class="c-loading">
        <div class="loader">
          Loading
        </div>
      </div>
      <div>
        <h1>Import</h1>
        <error-messages :errors="errors" :header-message="errorHeaderMessage" />
        <p class="import-products-modal__info">
          <a data-testid="download-template" href="#" @click="downloadTemplate">Download a CSV template with your inventory to upload</a><br>
          products in bulk (max {{ maxProducts }} products).
        </p>
      </div>
      <div class="import-products-modal__upload">
        <upload-files
          :file-types="fileTypes"
          @uploadedFile="uploadProductCsvFile"
          @error="handleUploadError"
        />
      </div>
    </div>
  </sb-modal>
</template>
<script>
import UploadFile from '@/components/utils/UploadFile'
import ErrorMessages from '@/components/utils/ErrorMessages'
import { ProductService } from '@/services/product/index'
import { currentDateFormat } from '@/utils/dateFormatter'
import { uploadFile } from './helper'
import { PlatformSourceMapper } from '@mappers'
import { formatDate } from '@/utils/common'

export default {
  name: 'ImportProductsToWro',
  components: {
    'upload-files': UploadFile,
    'error-messages': ErrorMessages
  },
  props: {
    openModal: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    uploadUrl: '',
    fileTypes: ['csv'],
    uploadedFiles: [],
    loading: false,
    errors: [],
    file: undefined,
    maxProducts: 100
  }),
  computed: {
    loggedInUser () {
      return this.$shipBobStore.getters['user/getLoggedInUser']
    },
    hasErrors () {
      return this.errors && this.errors.length > 0
    },
    hasSelectedFiles () {
      return this.files.length > 0
    },
    errorHeaderMessage () {
      if (this.errors.length === 1) {
        return 'Your file upload returned an error.'
      }

      return `Your file upload returned ${this.errors.length} errors.`
    }
  },
  mounted () {
    this.getSettingValue()
  },
  methods: {
    getSettingValue () {
      this.maxProducts = this.$shipBobStore.getters.enableMaxUploadExcelAtCreateWRO ? 500 : 100
    },
    uploadProductCsvFile (file) {
      this.file = file
      uploadFile(file, this.loadCsv, this.errorReadingCsv)
    },
    errorReadingCsv (event) {
      this.createError('Error while uploading file: ' + event.target.error.message)
    },
    async loadCsv (event) {
      const fileName = this.file.name
      const text = event.target.result
      if (this.validCsv(fileName, text)) {
        this.loading = true
        const uploadedProducts = this.csvToJson(text)
        const response = await this.$shipBobStore.dispatch('createWarehouseReceivingOrder/getProductWarnings', uploadedProducts)

        if (response.success) {
          this.processWarnings(response)
          if (!this.hasErrors) {
            const productsForTableSelection = this.toTableProducts(response.products)
            this.$shipBobStore.commit('createWarehouseReceivingOrder/setSelectedProducts', productsForTableSelection)
            this.$emit('closeModal')
          }
        } else {
          this.createError(response.errorMessage)
        }
        this.loading = false
      }
    },
    validFileType (fileName) {
      const fileExtensionRegex = fileName.match(/[0-9a-z]+$/i)
      if (!fileExtensionRegex) return false
      return this.fileTypes.includes(fileExtensionRegex[0].toLowerCase())
    },
    validateHeaderColumns (lines) {
      const currentline = lines[0].split(',')
      const headerColumns = [
        'InventoryId',
        'SKU',
        'ItemName',
        'IsLot (Yes/No value)',
        'QuantityToSend',
        'LotNumber',
        'ExpirationDate'
      ]
      return currentline.some((headerColumn, index) => this.sanitizeValue(headerColumn) !== headerColumns[index])
    },
    validCsv (fileName, csv) {
      this.resetErrors()
      const lines = csv.split('\n').filter(r => r)
      const totalRecords = lines.length - 1

      if (!this.validFileType(fileName)) {
        this.createError('Invalid file type.')
        return false
      }

      if (totalRecords < 1) {
        this.createError('File is empty. Please try again with another file.')
        return false
      }
      if (totalRecords > this.maxProducts) {
        this.createError(`Only ${this.maxProducts} records allowed to import.`)
      }

      if (this.validateHeaderColumns(lines)) {
        this.createError('File must contain "InventoryID, SKU, QuantityToSend, LotNumber, ExpirationDate" columns.')
      }

      return this.errors.length === 0
    },
    createError (message) {
      const index = this.errors.findIndex(
        (msg) => msg.message === message)

      if (index === -1) {
        this.errors.push({ message })
      }
    },
    csvToJson (csv) {
      const readNumberFromCsv = (stringToRead) => stringToRead ? parseInt(stringToRead.trim()) : 0
      const readStringFromCsv = (stringToRead) => stringToRead ? stringToRead.trim() : ''
      const readBooleanFromCsv = (stringToRead) => {
        if (stringToRead) {
          return stringToRead.trim().toLowerCase() === 'yes'
        }

        return undefined
      }
      const lines = csv.split('\n')
      const result = []

      for (let i = 1; i < lines.length; i++) {
        const currentLine = lines[i].split(',')
        if (currentLine.length === 7) {
          const inventoryId = readNumberFromCsv(this.sanitizeValue(currentLine[0]))
          const itemName = readStringFromCsv(currentLine[2])
          const isLot = readBooleanFromCsv(currentLine[3])
          const quantityToSend = readNumberFromCsv(currentLine[4])
          const lotNumber = readStringFromCsv(currentLine[5])
          const expirationDate = readStringFromCsv(this.sanitizeValue(currentLine[6]))

          result.push({
            key: i,
            inventoryId,
            itemName,
            isLot,
            quantityToSend,
            lotNumber,
            expirationDate
          })
        }
      }

      return result
    },
    toTableProducts (products) {
      const productDict = {}

      products.forEach(product => {
        const tempProduct = productDict[product.inventoryId]
        if (tempProduct) {
          tempProduct.quantitiesToSend.push({
            _id: tempProduct.quantitiesToSend.length + 1,
            amount: product.quantityToSend,
            lotNumber: product.lotNumber,
            expirationDate: formatDate(product.expirationDate)
          })
        } else {
          productDict[product.inventoryId] = {
            id: product.inventoryId,
            name: product.itemName,
            sku: product.sku,
            platform: PlatformSourceMapper.mapPlatformSourceFromAPI(product.platformId),
            isLotProduct: product.isLot,
            isDangerousGoods: !!product.isDangerousGoods,
            quantitiesToSend: [{
              _id: 1,
              amount: product.quantityToSend,
              lotNumber: product.lotNumber,
              expirationDate: formatDate(product.expirationDate)
            }]
          }
        }
      })

      return Object.values(productDict)
    },
    currentStep () {
      return this.$shipBobStore.getters['createWarehouseReceivingOrder/currentStep']
    },
    resetErrors () {
      this.errors = []
    },
    closeModal () {
      this.resetErrors()
      this.$emit('closeModal')
    },
    async downloadTemplate () {
      this.loading = true
      try {
        const response = await ProductService.getProductCsvTemplate()
        const objectUrl = window.URL.createObjectURL(new Blob([response]))
        const anchor = document.createElement('a')
        document.body.appendChild(anchor)
        anchor.target = '_blank'
        anchor.href = objectUrl
        anchor.download = `WRO_ProductUpload_Template_${currentDateFormat(new Date())}.csv`
        anchor.click()
        anchor.remove()
        this.loading = false
      } catch (e) {
        this.createError('Error while download template')
      } finally {
        this.loading = false
      }
    },
    handleUploadError (errorMessage) {
      this.createError(errorMessage)
    },
    processWarnings (apiResponse) {
      apiResponse.products.forEach(product => {
        if (product.warnings && product.warnings.length > 0) {
          product.warnings.forEach(warning => this.createError(warning))
        }
      })
    },
    sanitizeValue (value) {
      return value.trim().replace('\r', '').replace('"', '')
    }
  }
}
</script>
<style lang="scss" scoped>
.import-products-modal__body {
  position: relative;
}
.c-loading {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}
.loader {
  color: #2470AF;
  text-indent: -9999em;
  overflow: hidden;
  width: 1em;
  height: 1em;
  border-radius: 50%;
  margin: 72px auto;
  position: relative;
  -webkit-transform: translateZ(0);
  -ms-transform: translateZ(0);
  transform: translateZ(0);
  font-size: 30px;
  -webkit-animation: load6 1.7s infinite ease, round 1.7s infinite ease;
  animation: load6 1.7s infinite ease, round 1.7s infinite ease;
}
@-webkit-keyframes load6 {
  0% {
    box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
  5%,
  95% {
    box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
  10%,
  59% {
    box-shadow: 0 -0.83em 0 -0.4em, -0.087em -0.825em 0 -0.42em, -0.173em -0.812em 0 -0.44em, -0.256em -0.789em 0 -0.46em, -0.297em -0.775em 0 -0.477em;
  }
  20% {
    box-shadow: 0 -0.83em 0 -0.4em, -0.338em -0.758em 0 -0.42em, -0.555em -0.617em 0 -0.44em, -0.671em -0.488em 0 -0.46em, -0.749em -0.34em 0 -0.477em;
  }
  38% {
    box-shadow: 0 -0.83em 0 -0.4em, -0.377em -0.74em 0 -0.42em, -0.645em -0.522em 0 -0.44em, -0.775em -0.297em 0 -0.46em, -0.82em -0.09em 0 -0.477em;
  }
  100% {
    box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
}
@keyframes load6 {
  0% {
    box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
  5%,
  95% {
    box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
  10%,
  59% {
    box-shadow: 0 -0.83em 0 -0.4em, -0.087em -0.825em 0 -0.42em, -0.173em -0.812em 0 -0.44em, -0.256em -0.789em 0 -0.46em, -0.297em -0.775em 0 -0.477em;
  }
  20% {
    box-shadow: 0 -0.83em 0 -0.4em, -0.338em -0.758em 0 -0.42em, -0.555em -0.617em 0 -0.44em, -0.671em -0.488em 0 -0.46em, -0.749em -0.34em 0 -0.477em;
  }
  38% {
    box-shadow: 0 -0.83em 0 -0.4em, -0.377em -0.74em 0 -0.42em, -0.645em -0.522em 0 -0.44em, -0.775em -0.297em 0 -0.46em, -0.82em -0.09em 0 -0.477em;
  }
  100% {
    box-shadow: 0 -0.83em 0 -0.4em, 0 -0.83em 0 -0.42em, 0 -0.83em 0 -0.44em, 0 -0.83em 0 -0.46em, 0 -0.83em 0 -0.477em;
  }
}
.import-products-modal {
  z-index: 1000;

  &__body {
    display: flex;
    flex-flow: column wrap;
    justify-content: center;
    text-align: center;

    &__info {
      padding: 1em 0 0 0;
    }

    &__upload {
      padding: 2em 5em;
    }

    &__footer {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-evenly;
    }
  }
}
</style>
